// Restores scroll position on F5 / page reload. Vue Router's default
// scrollBehavior only provides `savedPosition` for back/forward (popstate),
// not for reloads, so without this every refresh lands at the top.
//
// Strategy: persist the latest scroll position to sessionStorage on
// `beforeunload`, keyed by full path. On the next initial scrollBehavior call
// (after a reload), restore it. Subsequent in-app navigations behave normally.
import type { RouterConfig } from '@nuxt/schema'
import { nextTick } from 'vue'

const STORAGE_KEY = 'nuxt:scroll-positions'

type PositionMap = Record<string, number>

function load(): PositionMap {
  if (typeof sessionStorage === 'undefined') return {}
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

function save(map: PositionMap) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    // sessionStorage may be unavailable (private mode, quota); ignore.
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    const map = load()
    map[location.pathname + location.search] = window.scrollY
    save(map)
  })
}

// Only restore on the very first scrollBehavior call after page load.
// Subsequent in-app navigations follow the normal "scroll to top" rule.
let isInitialLoad = true

type ScrollPosition = Awaited<ReturnType<NonNullable<RouterConfig['scrollBehavior']>>>

// Input events that mean the visitor has taken the viewport over. A `scroll`
// listener cannot be used for this: the router's own programmatic scroll and the
// browser's restore both emit `scroll`, so the guard would cancel itself.
const TAKEOVER_EVENTS = ['wheel', 'touchmove', 'keydown'] as const

function afterPageFinish(position: ScrollPosition) {
  if (typeof window === 'undefined') return position

  const nuxtApp = useNuxtApp()

  return new Promise<ScrollPosition>((resolve) => {
    // `page:finish` is only as prompt as the slowest thing the page suspends on,
    // and the timeout below fires regardless — so by the time this resolves the
    // visitor may well be reading halfway down. Scrolling them back then is worse
    // than never scrolling at all: if they have touched the page, stand down.
    const settle = (result: ScrollPosition) => {
      window.clearTimeout(timeout)
      for (const event of TAKEOVER_EVENTS) window.removeEventListener(event, cancel)
      resolve(result)
    }

    const cancel = () => settle(false)

    const timeout = window.setTimeout(() => settle(position), 3000)

    for (const event of TAKEOVER_EVENTS) {
      window.addEventListener(event, cancel, { passive: true, once: true })
    }

    nuxtApp.hooks.hookOnce('page:finish', async () => {
      await nextTick()
      settle(position)
    })
  })
}

export default <RouterConfig>{
  scrollBehavior(to, from, savedPosition) {
    // The first navigation of a document never scrolls to the top, and never
    // defers. A fresh document already starts at the top (Vue Router sets
    // scrollRestoration to 'manual'), so the "new navigation: top" rule below has
    // nothing to do — but Vue Router still applies it, and Nuxt only runs it once
    // hydration is done. On a slow page that lands a second or more after the page
    // was readable, yanking a visitor who had already started scrolling back to the
    // top. Returning `false` keeps wherever they are. `afterPageFinish()` is just as
    // wrong here: its `page:finish` has already fired by the time this runs, so it
    // waits out the full 3s timeout and jumps even later.
    //
    // The two positions a fresh document DOES owe the visitor — an anchor and the
    // F5 restore — are returned directly by the branches below.
    const initial = isInitialLoad
    if (initial) isInitialLoad = false
    const deferToPage = (position: ScrollPosition) => (initial ? false : afterPageFinish(position))

    // Same-page hash change: let the browser scroll to the anchor.
    if (to.hash && to.path === from.path) {
      return { el: to.hash }
    }

    // F5 / refresh only: restore from sessionStorage. A forced full-page
    // navigation (logo `external`, typed-in URL) is navigation type 'navigate',
    // not 'reload' — those should land at the top, so we let them fall through.
    // Without this guard, `isInitialLoad` fires for ANY full document load and
    // wrongly restores the last scroll position on a fresh "go home" click.
    if (initial && typeof window !== 'undefined') {
      const navType = performance.getEntriesByType('navigation')[0]?.type
      if (navType === 'reload') {
        const saved = load()[to.fullPath]
        if (saved != null) return { left: 0, top: saved }
      }
    }

    // Back/forward: Vue Router provides the previous position.
    if (savedPosition) return deferToPage(savedPosition)

    // Same-path, query-only navigation (catalog pagination / filters):
    // keep the current scroll position. The page handles its own scroll
    // (e.g. ProductCatalog scrolls to the filter bar), so a forced jump to
    // top here only fights that and looks like the page reloaded.
    if (to.path === from.path) return false

    if (to.hash) {
      return initial ? { el: to.hash } : afterPageFinish({ el: to.hash })
    }

    // New in-app navigation: top.
    return deferToPage({ left: 0, top: 0 })
  },
}
